/**
 * CL-385D — Per-collaborator review/action requests on pmProjectFiles.reviewRequests[].
 * No new collection. File access remains projectFileShares; this controls Needs Attention only.
 */

import { needsClientApproval, normalizeReviewStatus } from "./approvalUtils";
import { collaboratorShareColKey } from "./projectFileShareIds";

function safeStr(value) {
  return String(value ?? "").trim();
}

function safeEmail(value) {
  return safeStr(value).toLowerCase();
}

export const REVIEW_REQUEST_TYPES = Object.freeze({
  NONE: "none",
  REVIEW: "review",
  APPROVAL: "approval",
});

export const REVIEW_REQUEST_STATUSES = Object.freeze({
  OPEN: "open",
  RESPONDED: "responded",
  CLOSED: "closed",
  CANCELLED: "cancelled",
});

/** CL-385E — persisted on reviewRequests[].responseType when status is responded. */
export const REVIEW_RESPONSE_TYPES = Object.freeze({
  REVIEWED_NO_COMMENTS: "reviewed_no_comments",
  COMMENT: "comment",
  CHANGES_NEEDED: "changes_needed",
  APPROVED: "approved",
  REQUEST_CHANGES: "request_changes",
  REVIEWED_NO_DECISION: "reviewed_no_decision",
});

export const REVIEWER_RESPONSE_HINT =
  "Use these actions to let Mark know you've reviewed this file. Your response clears it from your Needs Attention list.";

export const REVIEWER_RESPONSE_SUCCESS_MESSAGE =
  "Response submitted. This file has been cleared from your Needs Attention list.";

export const REVIEW_REQUEST_ACTION_OPTIONS = [
  { value: REVIEW_REQUEST_TYPES.NONE, label: "No action required (clear client review)" },
  { value: REVIEW_REQUEST_TYPES.REVIEW, label: "Request review / comment" },
  { value: REVIEW_REQUEST_TYPES.APPROVAL, label: "Request client review" },
];

/** Stable id for a roster/share row. */
export function buildCollaboratorRequestKey({ userId, email } = {}) {
  const uid = safeStr(userId);
  const em = safeEmail(email);
  if (uid) return `uid:${uid}`;
  if (em) return `email:${em}`;
  return "";
}

export function isClientCollaboratorRole(roleRaw) {
  const r = safeStr(roleRaw).toLowerCase();
  return r === "client" || r === "project_admin";
}

export function isContributorCollaboratorRole(roleRaw) {
  const r = safeStr(roleRaw).toLowerCase();
  return ["designer", "engineer", "supplier", "contractor", "consultant", "reviewer", "trade", "subtrade", "collaborator"].includes(
    r
  );
}

/** Approval only for client-facing roles (V1). */
export function normalizeActionForRole(requestType, roleRaw) {
  const t = safeStr(requestType).toLowerCase() || REVIEW_REQUEST_TYPES.NONE;
  if (t === REVIEW_REQUEST_TYPES.APPROVAL && !isClientCollaboratorRole(roleRaw)) {
    return REVIEW_REQUEST_TYPES.REVIEW;
  }
  if (t === REVIEW_REQUEST_TYPES.REVIEW || t === REVIEW_REQUEST_TYPES.APPROVAL) return t;
  return REVIEW_REQUEST_TYPES.NONE;
}

export function normalizeReviewRequests(fileMeta) {
  const raw = fileMeta?.reviewRequests;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row, index) => {
      const id = safeStr(row?.id) || buildCollaboratorRequestKey({ userId: row?.userId, email: row?.email });
      if (!id) return null;
      const requestType = normalizeActionForRole(row?.requestType, row?.role);
      const status = safeStr(row?.status).toLowerCase() || REVIEW_REQUEST_STATUSES.OPEN;
      return {
        id,
        userId: safeStr(row?.userId) || null,
        email: safeEmail(row?.email || row?.emailLower) || null,
        role: safeStr(row?.role) || null,
        requestType,
        status:
          status === REVIEW_REQUEST_STATUSES.RESPONDED ||
          status === REVIEW_REQUEST_STATUSES.CLOSED ||
          status === REVIEW_REQUEST_STATUSES.CANCELLED
            ? status
            : REVIEW_REQUEST_STATUSES.OPEN,
        requestedAt: row?.requestedAt ?? null,
        requestedByUid: safeStr(row?.requestedByUid) || null,
        requestedByEmail: safeStr(row?.requestedByEmail) || null,
        respondedAt: row?.respondedAt ?? null,
        responseType: safeStr(row?.responseType) || null,
        _index: index,
      };
    })
    .filter(Boolean);
}

export function hasPerCollaboratorReviewRequests(fileMeta) {
  return normalizeReviewRequests(fileMeta).length > 0;
}

/**
 * @typedef {object} ReviewViewerContext
 * @property {string} [uid]
 * @property {string} [email]
 * @property {string} [role]
 * @property {boolean} [isContributor]
 * @property {boolean} [isClient]
 */

export function buildReviewViewerContext({ uid, email, role, isContributor, isClient } = {}) {
  return {
    uid: safeStr(uid),
    email: safeEmail(email),
    role: safeStr(role),
    isContributor: !!isContributor,
    isClient: !!isClient,
  };
}

/** Merge portal viewer context with auth user for request matching (CL-385F). */
export function mergeViewerMatchContext(viewer, authUser) {
  const uid = safeStr(viewer?.uid || authUser?.uid);
  const email = safeEmail(
    viewer?.email || authUser?.email || authUser?.emailLower || viewer?.emailLower
  );
  return {
    ...(viewer || {}),
    uid,
    email,
  };
}

function viewerMatchesRequest(viewer, request) {
  const vUid = safeStr(viewer?.uid);
  const vEmail = safeEmail(viewer?.email);
  const rUid = safeStr(request?.userId);
  const rEmail = safeEmail(request?.email || request?.emailLower);
  if (vUid && rUid && vUid === rUid) return true;
  if (vEmail && rEmail && vEmail === rEmail) return true;
  const rid = safeStr(request?.id);
  if (rid) {
    if (vUid && rid === `uid:${vUid}`) return true;
    if (vEmail && rid === `email:${vEmail}`) return true;
  }
  const vKey = buildCollaboratorRequestKey({ userId: vUid, email: vEmail });
  if (vKey && rid && (vKey === rid || rid.startsWith(`${vKey}#`))) return true;
  return false;
}

/** True when viewer has an open review or approval assignment (CL-386G). */
export function viewerHasOpenAssignedReviewRequest(fileMeta, viewer) {
  return !!getOpenReviewRequestForViewer(fileMeta, viewer);
}

export function getViewerRespondedRequestForViewer(fileMeta, viewer) {
  const requests = normalizeReviewRequests(fileMeta);
  return (
    requests.find(
      (r) =>
        viewerMatchesRequest(viewer, r) &&
        r.status === REVIEW_REQUEST_STATUSES.RESPONDED &&
        (r.requestType === REVIEW_REQUEST_TYPES.REVIEW || r.requestType === REVIEW_REQUEST_TYPES.APPROVAL)
    ) || null
  );
}

export function getOpenReviewRequestForViewer(fileMeta, viewer) {
  const requests = normalizeReviewRequests(fileMeta);
  return (
    requests.find(
      (r) =>
        viewerMatchesRequest(viewer, r) &&
        r.status === REVIEW_REQUEST_STATUSES.OPEN &&
        (r.requestType === REVIEW_REQUEST_TYPES.REVIEW || r.requestType === REVIEW_REQUEST_TYPES.APPROVAL)
    ) || null
  );
}

/**
 * Portal / list attention (CL-LIVE-002):
 * Only an explicit open reviewRequests[] assignment for this viewer creates attention.
 * Sharing, upload, filename, and legacy pending_client_review alone do not.
 */
export function fileNeedsAttentionForViewer(fileMeta, viewer) {
  const f = fileMeta || {};
  if (safeStr(f.status).toLowerCase() === "deleted") return false;
  if (!viewer) return false;
  return !!getOpenReviewRequestForViewer(f, viewer);
}

/**
 * Seed admin drawer rows from legacy global pending when no per-collaborator array yet.
 */
export function seedReviewRequestsFromLegacy(fileMeta, collaborators = [], shares = []) {
  const existing = normalizeReviewRequests(fileMeta);
  if (existing.length) return existing;

  if (!needsClientApproval(fileMeta)) return [];

  const kind = safeStr(fileMeta?.reviewRequestKind).toLowerCase();
  const requestType =
    kind === "approval" ? REVIEW_REQUEST_TYPES.APPROVAL : REVIEW_REQUEST_TYPES.REVIEW;
  const fileId = safeStr(fileMeta?.id);
  const shared = (shares || []).filter((s) => safeStr(s.fileId) === fileId);
  const roster = Array.isArray(collaborators) ? collaborators : [];
  const out = [];

  const targets = shared.length ? shared : roster.map((c) => ({ sharedWithUid: c?.uid, sharedWithEmail: c?.email }));

  for (const row of targets) {
    const uid = safeStr(row?.sharedWithUid || row?.userId || row?.uid);
    const email = safeEmail(row?.sharedWithEmail || row?.email || row?.emailLower);
    const collab =
      roster.find((c) => safeStr(c?.uid || c?.userId) === uid) ||
      roster.find((c) => safeEmail(c?.email || c?.emailLower) === email) ||
      null;
    const role = safeStr(collab?.role || collab?.constructionRole || row?.role);
    if (!isClientCollaboratorRole(role) && requestType === REVIEW_REQUEST_TYPES.APPROVAL) continue;
    const id = buildCollaboratorRequestKey({ userId: uid, email });
    if (!id || out.some((x) => x.id === id)) continue;
    out.push({
      id,
      userId: uid || null,
      email: email || null,
      role: role || null,
      requestType: normalizeActionForRole(requestType, role),
      status: REVIEW_REQUEST_STATUSES.OPEN,
      requestedAt: fileMeta?.reviewRequestedAt ?? null,
      requestedByUid: safeStr(fileMeta?.reviewRequestedByUid) || null,
      requestedByEmail: safeStr(fileMeta?.reviewRequestedByEmail) || null,
      respondedAt: null,
      responseType: null,
    });
  }

  return out;
}

function requestBelongsToCollaboratorRow(request, baseId) {
  const id = safeStr(baseId);
  const rid = safeStr(request?.id);
  return rid === id || rid.startsWith(`${id}#`);
}

/** Default share flags when admin enables file access (CL-386F). */
export function defaultShareFlagsForRole(roleRaw) {
  const client = isClientCollaboratorRole(roleRaw);
  return {
    canComment: true,
    canUploadRevision: !client,
  };
}

/** Latest persisted request row for a collaborator (open beats responded for editing). */
export function getCollaboratorRequestHistoryForRow(existingRequests, baseId) {
  const related = normalizeReviewRequests({ reviewRequests: existingRequests }).filter((r) =>
    requestBelongsToCollaboratorRow(r, baseId)
  );
  const open = related.find((r) => r.status === REVIEW_REQUEST_STATUSES.OPEN);
  const responded = [...related]
    .reverse()
    .find((r) => r.status === REVIEW_REQUEST_STATUSES.RESPONDED);
  return { open: open || null, responded: responded || null, related };
}

/**
 * Admin unified workflow draft rows — file access + reviewer assignment (CL-386F).
 * @param {object} fileMeta
 * @param {object[]} collaborators
 * @param {object[]} shareDraftRows — editFileShares-shaped rows (shareColKey, isShared, canComment, …)
 */
export function buildReviewWorkflowDraftRows(fileMeta, collaborators = [], shareDraftRows = []) {
  const shareByColKey = new Map();
  (shareDraftRows || []).forEach((row) => {
    const key = safeStr(row?.shareColKey);
    if (key) shareByColKey.set(key, row);
  });

  const existing = loadReviewRequestsForAdmin(fileMeta, collaborators, shareDraftRows);

  return (collaborators || []).map((collaborator, index) => {
    const uid = safeStr(collaborator?.userId || collaborator?.uid || collaborator?.authUid);
    const email = safeStr(collaborator?.email || collaborator?.emailLower).toLowerCase();
    const colKey =
      collaboratorShareColKey(collaborator) || (uid ? `u:${uid}` : email ? `e:${email}` : `row-${index}`);
    const shareDraft = shareByColKey.get(colKey);
    const isShared = !!shareDraft?.isShared;
    const role = safeStr(collaborator?.role || collaborator?.constructionRole || collaborator?.primaryUserType);
    const id = buildCollaboratorRequestKey({ userId: uid, email });
    const { open, responded } = getCollaboratorRequestHistoryForRow(existing, id);
    const prev = open || responded || null;
    const requestType =
      open?.status === REVIEW_REQUEST_STATUSES.OPEN
        ? normalizeActionForRole(open.requestType, role)
        : REVIEW_REQUEST_TYPES.NONE;
    const shareDefaults = defaultShareFlagsForRole(role);

    return {
      id,
      colKey,
      shareColKey: colKey,
      displayName: safeStr(collaborator?.displayName || collaborator?.name || email || "Collaborator"),
      email,
      userId: uid,
      role,
      isShared,
      canComment: isShared ? shareDraft?.canComment !== false : shareDefaults.canComment,
      canUploadRevision: isShared ? shareDraft?.canUploadRevision !== false : shareDefaults.canUploadRevision,
      requestType,
      prevRequestType: prev?.requestType || REVIEW_REQUEST_TYPES.NONE,
      prevStatus: prev?.status || "",
      hadOpenRequest: open?.status === REVIEW_REQUEST_STATUSES.OPEN,
      requestedAt: open?.requestedAt ?? prev?.requestedAt ?? null,
      requestedByUid: open?.requestedByUid ?? prev?.requestedByUid ?? null,
      requestedByEmail: open?.requestedByEmail ?? prev?.requestedByEmail ?? null,
      prevResponseType: responded?.responseType || prev?.responseType || null,
      lastRespondedAt: responded?.respondedAt ?? null,
    };
  });
}

function formatPreviousDraftRowResponseLabel(row) {
  const who = roleDisplayName(row?.role);
  const action = getResponseActionLabel(row?.prevResponseType);
  const when = formatShortViewerRespondedDate(row?.lastRespondedAt);
  if (when) return `Previous: ${who} · ${action} · ${when}`;
  return `Previous: ${who} · ${action}`;
}

/**
 * Draft-row current assignment + prior response lines (CL-388A).
 * @returns {{ lines: Array<{ text: string, tone: string }> }}
 */
export function resolveAdminDraftRowCurrentPreviousPresentation(row) {
  if (!row?.isShared) {
    return { lines: [{ text: "Not shared", tone: "neutral" }] };
  }

  const lines = [];
  const requestType = normalizeActionForRole(row?.requestType, row?.role);
  if (requestType !== REVIEW_REQUEST_TYPES.NONE) {
    lines.push(
      getAdminRequestStatusPresentation({
        role: row?.role,
        requestType,
        status: REVIEW_REQUEST_STATUSES.OPEN,
      })
    );
  }

  if (row?.prevResponseType) {
    lines.push({
      text: formatPreviousDraftRowResponseLabel(row),
      tone: "previous",
    });
  }

  if (!lines.length) {
    lines.push({ text: "Shared only", tone: "shared" });
  }

  return { lines };
}

/** Draft-row status chip for unified workflow table (CL-386F). */
export function resolveAdminDraftRowStatusPresentation(row) {
  const { lines } = resolveAdminDraftRowCurrentPreviousPresentation(row);
  return lines[0] || { text: "Shared only", tone: "shared" };
}

/**
 * Build persisted reviewRequests[] from admin drawer draft rows.
 * Preserves responded/closed history; re-requests create a new open id (CL-386F).
 */
export function buildReviewRequestsFromAdminRows(rows = [], actor = {}, now = null, existingRequests = []) {
  const ts = now instanceof Date ? now : new Date();
  const existing = normalizeReviewRequests({ reviewRequests: existingRequests });
  const historical = existing.filter(
    (r) =>
      r.status === REVIEW_REQUEST_STATUSES.RESPONDED ||
      r.status === REVIEW_REQUEST_STATUSES.CLOSED ||
      r.status === REVIEW_REQUEST_STATUSES.CANCELLED
  );
  const out = [...historical];
  const addedOpenIds = new Set();

  for (const row of rows) {
    const baseId = safeStr(row?.id) || buildCollaboratorRequestKey({ userId: row?.userId, email: row?.email });
    if (!baseId) continue;
    const role = safeStr(row?.role);
    const requestType = normalizeActionForRole(row?.requestType, role);
    if (requestType === REVIEW_REQUEST_TYPES.NONE || !row?.isShared) continue;

    const prevStatus = safeStr(row?.prevStatus).toLowerCase();
    const prevType = safeStr(row?.prevRequestType).toLowerCase();
    const unchangedOpen =
      prevStatus === REVIEW_REQUEST_STATUSES.OPEN &&
      prevType === requestType &&
      row?.hadOpenRequest;

    if (unchangedOpen) {
      const keep = existing.find(
        (r) =>
          r.id === baseId &&
          r.status === REVIEW_REQUEST_STATUSES.OPEN &&
          r.requestType === requestType
      );
      if (keep && !addedOpenIds.has(keep.id)) {
        out.push(keep);
        addedOpenIds.add(keep.id);
      }
      continue;
    }

    const hadResponded =
      prevStatus === REVIEW_REQUEST_STATUSES.RESPONDED ||
      historical.some(
        (r) => requestBelongsToCollaboratorRow(r, baseId) && r.status === REVIEW_REQUEST_STATUSES.RESPONDED
      );
    const openId = hadResponded ? `${baseId}#${ts.getTime()}` : baseId;
    if (addedOpenIds.has(openId)) continue;

    out.push({
      id: openId,
      userId: safeStr(row?.userId) || null,
      email: safeEmail(row?.email) || null,
      role: role || null,
      requestType,
      status: REVIEW_REQUEST_STATUSES.OPEN,
      requestedAt: ts,
      requestedByUid: safeStr(actor.requestedByUid) || null,
      requestedByEmail: safeStr(actor.requestedByEmail) || null,
      respondedAt: null,
      responseType: null,
    });
    addedOpenIds.add(openId);
  }

  return out;
}

/** Sync legacy global fields for backward-compatible readers. */
export function syncLegacyReviewFieldsFromRequests(requests = []) {
  const open = (requests || []).filter((r) => r.status === REVIEW_REQUEST_STATUSES.OPEN);
  const openApproval = open.find((r) => r.requestType === REVIEW_REQUEST_TYPES.APPROVAL);
  const openReview = open.find((r) => r.requestType === REVIEW_REQUEST_TYPES.REVIEW);

  if (openApproval) {
    return {
      reviewStatus: "pending_client_review",
      reviewRequestKind: "approval",
      clearLegacyRequest: false,
    };
  }
  if (openReview) {
    return {
      reviewStatus: "pending_client_review",
      reviewRequestKind: "review",
      clearLegacyRequest: false,
    };
  }

  return {
    reviewStatus: "for_information",
    reviewRequestKind: null,
    clearLegacyRequest: true,
  };
}

function normGlobalReviewStatus(value) {
  return safeStr(value).toLowerCase().replace(/\s+/g, "_").replace(/-+/g, "_");
}

/**
 * Admin save: sync global reviewStatus from open requests without downgrading active workflow
 * when collaborators respond (CL-385H).
 */
export function resolveGlobalReviewStatusAfterRequestSync(requests, currentReviewStatus) {
  const open = (requests || []).filter(
    (r) => r.status === REVIEW_REQUEST_STATUSES.OPEN && r.requestType !== REVIEW_REQUEST_TYPES.NONE
  );
  const openApproval = open.find((r) => r.requestType === REVIEW_REQUEST_TYPES.APPROVAL);
  const openReview = open.find((r) => r.requestType === REVIEW_REQUEST_TYPES.REVIEW);

  if (openApproval) {
    return {
      reviewStatus: "pending_client_review",
      reviewRequestKind: "approval",
      clearLegacyRequest: false,
    };
  }
  if (openReview) {
    return {
      reviewStatus: "pending_client_review",
      reviewRequestKind: "review",
      clearLegacyRequest: false,
    };
  }

  const cur = normGlobalReviewStatus(currentReviewStatus);
  // CL-LIVE-002: do not preserve sticky pending_client_review when no open assignments remain.
  const preserveStatuses = [
    "under_review",
    "in_review",
    "approved",
    "approved_as_noted",
    "revisions_required",
    "changes_requested",
    "changes_submitted",
    "returned",
    "rejected",
  ];
  if (preserveStatuses.includes(cur)) {
    return {
      reviewStatus: cur,
      reviewRequestKind: null,
      clearLegacyRequest: true,
    };
  }

  return {
    reviewStatus: "for_information",
    reviewRequestKind: null,
    clearLegacyRequest: true,
  };
}

function normalizeResponseType(value) {
  const t = safeStr(value).toLowerCase();
  const allowed = Object.values(REVIEW_RESPONSE_TYPES);
  if (allowed.includes(t)) return t;
  if (t === "approved") return REVIEW_RESPONSE_TYPES.APPROVED;
  if (t === "under_review") return REVIEW_RESPONSE_TYPES.REVIEWED_NO_DECISION;
  if (t === "changes_requested" || t === "revisions_required") return REVIEW_RESPONSE_TYPES.REQUEST_CHANGES;
  return REVIEW_RESPONSE_TYPES.COMMENT;
}

/** Map client formal decision keys to reviewRequests responseType (CL-385E). */
export function mapClientDecisionToResponseType(decision) {
  const d = safeStr(decision).toLowerCase();
  if (d === "approved") return REVIEW_RESPONSE_TYPES.APPROVED;
  if (d === "under_review") return REVIEW_RESPONSE_TYPES.REVIEWED_NO_DECISION;
  if (d === "changes_requested" || d === "revisions_required") return REVIEW_RESPONSE_TYPES.REQUEST_CHANGES;
  return REVIEW_RESPONSE_TYPES.COMMENT;
}

export function markViewerRequestResponded(fileMeta, viewer, responseType = REVIEW_RESPONSE_TYPES.COMMENT) {
  const { reviewRequests } = applyViewerReviewResponse(fileMeta, viewer, responseType);
  return reviewRequests;
}

/** User-facing label for confirm dialog and status chip (CL-385F). */
export function getResponseActionLabel(responseType) {
  const t = normalizeResponseType(responseType);
  if (t === REVIEW_RESPONSE_TYPES.REVIEWED_NO_COMMENTS) return "Reviewed — no comments";
  if (t === REVIEW_RESPONSE_TYPES.REVIEWED_NO_DECISION) return "Reviewed — no decision yet";
  if (t === REVIEW_RESPONSE_TYPES.CHANGES_NEEDED) return "Changes / clarification needed";
  if (t === REVIEW_RESPONSE_TYPES.REQUEST_CHANGES) return "Request changes";
  if (t === REVIEW_RESPONSE_TYPES.APPROVED) return "Approve";
  if (t === REVIEW_RESPONSE_TYPES.COMMENT) return "Add comment";
  return "Submit response";
}

export function formatViewerRespondedChipLabel(responseType) {
  return `You responded: ${getResponseActionLabel(responseType)}`;
}

function toSafeDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Short date for drawer response record (e.g. "Jun 4"). */
export function formatShortViewerRespondedDate(respondedAt) {
  const d = toSafeDate(respondedAt);
  if (!d) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric" });
}

/** Drawer status line: "You responded: Reviewed — no comments · Jun 4" (CL-385G). */
export function formatViewerRespondedRecordLabel(respondedRequest) {
  if (!respondedRequest?.responseType) return null;
  const action = getResponseActionLabel(respondedRequest.responseType);
  const when = formatShortViewerRespondedDate(respondedRequest.respondedAt);
  return when ? `You responded: ${action} · ${when}` : formatViewerRespondedChipLabel(respondedRequest.responseType);
}

/** One-click assigned responses use reviewRequests[] as canonical record — no thread duplicate (CL-385G). */
export function shouldCreateThreadEntryForAssignedResponse(responseType) {
  const t = normalizeResponseType(responseType);
  return (
    t === REVIEW_RESPONSE_TYPES.COMMENT ||
    t === REVIEW_RESPONSE_TYPES.CHANGES_NEEDED ||
    t === REVIEW_RESPONSE_TYPES.REQUEST_CHANGES
  );
}

export const ASSIGNED_REVIEW_RESPONSE_THREAD_KIND = "review_response";

/** Thread row shape when a comment-style assigned response is persisted. */
export function buildAssignedReviewResponseThreadEntry(responseType, authorMeta = {}) {
  const normalizedType = normalizeResponseType(responseType);
  return {
    ...authorMeta,
    kind: ASSIGNED_REVIEW_RESPONSE_THREAD_KIND,
    responseType: normalizedType,
    body: getResponseActionLabel(responseType),
  };
}

/** True when viewer already responded and there is no open assigned request (idempotent submit). */
export function viewerAssignedReviewResponseAlreadyRecorded(fileMeta, viewer) {
  return !getOpenReviewRequestForViewer(fileMeta, viewer) && !!getViewerRespondedRequestForViewer(fileMeta, viewer);
}

function entryAuthMatchesViewer(entry, viewer) {
  const vUid = safeStr(viewer?.uid);
  const vEmail = safeEmail(viewer?.email);
  const aUid = safeStr(entry?.authorUid);
  const aEmail = safeEmail(entry?.authorEmail);
  if (vUid && aUid && vUid === aUid) return true;
  return !!(vEmail && aEmail && vEmail === aEmail);
}

/** Hide thread cards that duplicate the canonical assigned-response record (CL-385G). */
export function isRedundantAssignedResponseThreadEntry(entry, fileMeta, viewer) {
  const responded = getViewerRespondedRequestForViewer(fileMeta, viewer);
  if (!responded?.responseType || !entry) return false;
  if (!entryAuthMatchesViewer(entry, viewer)) return false;

  const canonicalLabel = getResponseActionLabel(responded.responseType);
  const body = safeStr(entry?.body);
  const entryType = normalizeResponseType(entry?.responseType);
  const respondedType = normalizeResponseType(responded.responseType);

  if (entry?.kind === ASSIGNED_REVIEW_RESPONSE_THREAD_KIND || entry?.kind === "review_response") {
    return entryType === respondedType || body === canonicalLabel;
  }
  if (entry?.kind === "comment" && body === canonicalLabel) return true;
  return false;
}

export function filterDiscussionEntriesHideAssignedResponseDuplicates(entries, fileMeta, viewer) {
  const list = Array.isArray(entries) ? entries : [];
  const responded = getViewerRespondedRequestForViewer(fileMeta, viewer);
  if (!responded) return list;
  return list.filter((entry) => !isRedundantAssignedResponseThreadEntry(entry, fileMeta, viewer));
}

/**
 * Apply response to in-memory file + return whether an open request was matched (CL-385F).
 */
export function applyViewerReviewResponse(fileMeta, viewer, responseType, extraFileFields = {}) {
  const normalizedType = normalizeResponseType(responseType);
  const before = normalizeReviewRequests(fileMeta);

  let matched = false;
  const reviewRequests = before.map((r) => {
    if (!viewerMatchesRequest(viewer, r)) return r;
    if (r.status !== REVIEW_REQUEST_STATUSES.OPEN) return r;
    matched = true;
    return {
      ...r,
      status: REVIEW_REQUEST_STATUSES.RESPONDED,
      respondedAt: new Date(),
      responseType: normalizedType,
    };
  });

  const legacySync = syncLegacyReviewFieldsFromRequests(reviewRequests);
  const file = {
    ...(fileMeta || {}),
    ...extraFileFields,
    reviewRequests,
  };
  // CL-385H: only client formal decisions pass reviewStatus in extraFileFields — never auto-sync from requests.
  if (extraFileFields.reviewStatus !== undefined) {
    file.reviewStatus = extraFileFields.reviewStatus;
  }
  if (legacySync.clearLegacyRequest) {
    delete file.reviewRequestKind;
    delete file.reviewRequestedAt;
    delete file.reviewRequestedByUid;
    delete file.reviewRequestedByEmail;
  } else if (legacySync.reviewRequestKind) {
    file.reviewRequestKind = legacySync.reviewRequestKind;
  }

  return {
    file,
    reviewRequests,
    legacySync,
    matched: matched || !hasPerCollaboratorReviewRequests(fileMeta),
  };
}

/**
 * Firestore patch for collaborator review responses.
 * Keys must stay within firestore.rules collaborator allow-list (reviewResponse* + reviewRequests + reviewStatus + updatedAt).
 * Legacy reviewRequestKind fields are not collaborator-writable — cleared in local state only.
 */
export function buildCollaboratorReviewResponseFilePatch({
  reviewRequests,
  legacySync,
  responseType,
  viewerUid,
  viewerEmail,
}) {
  const normalizedType = normalizeResponseType(responseType);
  const patch = {
    reviewRequests,
    reviewResponseType: normalizedType,
    reviewResponseByUid: viewerUid,
    reviewResponseByEmail: viewerEmail || null,
    reviewResponseNote: getResponseActionLabel(responseType),
  };
  return patch;
}

/** Client Firestore patch for per-collaborator request updates (no global reviewStatus writes). */
export function buildClientPerCollaboratorFilePatch({ reviewRequests }) {
  return { reviewRequests };
}

/**
 * Firestore patch for assigned review responses — client vs collaborator allow-lists (CL-408Q).
 */
export function buildAssignedReviewResponseFirestorePatch({
  decisionRole = "contributor",
  reviewRequests,
  legacySync,
  responseType,
  viewerUid,
  viewerEmail,
}) {
  if (decisionRole === "decisionMaker") {
    return buildClientPerCollaboratorFilePatch({ reviewRequests });
  }
  return buildCollaboratorReviewResponseFilePatch({
    reviewRequests,
    legacySync,
    responseType,
    viewerUid,
    viewerEmail,
  });
}

/** Drawer “Your review status” block for assigned open/responded requests (CL-385H). */
export function getViewerAssignedReviewStatusPresentation(fileMeta, viewer) {
  const open = getOpenReviewRequestForViewer(fileMeta, viewer);
  if (open) {
    return { heading: "Your review status", label: "Action required", variant: "open" };
  }
  const responded = getViewerRespondedRequestForViewer(fileMeta, viewer);
  if (responded?.responseType) {
    return {
      heading: "Your review status",
      label: `You responded: ${getResponseActionLabel(responded.responseType)}`,
      variant: "responded",
    };
  }
  return null;
}

/** Load all persisted requests for admin UI (includes responded rows). */
export function loadReviewRequestsForAdmin(fileMeta, collaborators = [], shares = []) {
  const existing = normalizeReviewRequests(fileMeta);
  if (existing.length) return existing;
  return seedReviewRequestsFromLegacy(fileMeta, collaborators, shares);
}

function roleDisplayName(roleRaw) {
  const b = roleBucket(roleRaw);
  if (b === "client") return "Client";
  if (b === "engineer") return "Engineer";
  if (b === "designer") return "Designer";
  return "Collaborator";
}

function formatResponseTypeLabel(responseType) {
  const t = safeStr(responseType).toLowerCase();
  if (t === REVIEW_RESPONSE_TYPES.REVIEWED_NO_COMMENTS) return "reviewed — no comments";
  if (t === REVIEW_RESPONSE_TYPES.REVIEWED_NO_DECISION) return "reviewed — no decision yet";
  if (t === REVIEW_RESPONSE_TYPES.CHANGES_NEEDED) return "changes / clarification needed";
  if (t === REVIEW_RESPONSE_TYPES.REQUEST_CHANGES) return "requested changes";
  if (t === REVIEW_RESPONSE_TYPES.APPROVED) return "approved";
  if (t === REVIEW_RESPONSE_TYPES.COMMENT) return "commented";
  return "responded";
}

/** One-line admin status for a collaborator request row. */
export function formatCollaboratorRequestAdminStatus(request) {
  if (!request || request.requestType === REVIEW_REQUEST_TYPES.NONE || safeStr(request.status) === "none") {
    return "Shared only";
  }
  const who = roleDisplayName(request.role);
  if (request.status === REVIEW_REQUEST_STATUSES.OPEN) {
    return `Waiting on ${who}`;
  }
  if (request.status === REVIEW_REQUEST_STATUSES.RESPONDED) {
    const action = getResponseActionLabel(request.responseType) || formatResponseTypeLabel(request.responseType);
    return `${who} responded: ${action}`;
  }
  return `${who} — ${request.status}`;
}

/** Chip tone for admin status presentation (CL-385G). */
export function getAdminRequestStatusPresentation(request) {
  const text = formatCollaboratorRequestAdminStatus(request);
  if (!request || request.requestType === REVIEW_REQUEST_TYPES.NONE || safeStr(request.status) === "none") {
    return { text, tone: "shared" };
  }
  if (request.status === REVIEW_REQUEST_STATUSES.OPEN) {
    return { text, tone: "waiting" };
  }
  if (request.status === REVIEW_REQUEST_STATUSES.RESPONDED) {
    return { text, tone: "responded" };
  }
  return { text, tone: "neutral" };
}

export function adminRequestStatusChipClassName(tone) {
  if (tone === "waiting") return "border-amber-200 bg-amber-50 text-amber-950";
  if (tone === "responded") return "border-emerald-200 bg-emerald-50 text-emerald-900";
  if (tone === "previous") return "border-slate-200 bg-slate-50 text-slate-600";
  if (tone === "shared") return "border-slate-200 bg-slate-50 text-slate-600";
  return "border-slate-200 bg-white text-slate-700";
}

/** Compact status lines for admin file summary (Review tab chips). */
export function buildAdminRequestStatusLines(fileMeta) {
  const requests = normalizeReviewRequests(fileMeta).filter(
    (r) => r.requestType === REVIEW_REQUEST_TYPES.REVIEW || r.requestType === REVIEW_REQUEST_TYPES.APPROVAL
  );
  if (!requests.length && needsClientApproval(fileMeta)) {
    return ["Waiting on client (legacy)"];
  }
  return requests.map((r) => formatCollaboratorRequestAdminStatus(r));
}

function roleBucket(roleRaw) {
  const r = safeStr(roleRaw).toLowerCase();
  if (r === "client" || r === "project_admin") return "client";
  if (r === "engineer") return "engineer";
  if (r === "designer") return "designer";
  return "other";
}

/** Admin file-level summary labels. */
export function buildFileReviewRequestAdminLabels(fileMeta) {
  const requests = normalizeReviewRequests(fileMeta);
  const lines = buildAdminRequestStatusLines(fileMeta);

  const actionable = requests.filter(
    (r) => r.requestType === REVIEW_REQUEST_TYPES.REVIEW || r.requestType === REVIEW_REQUEST_TYPES.APPROVAL
  );
  const chipPresentations = actionable.map((r) => getAdminRequestStatusPresentation(r));

  if (!requests.length && !needsClientApproval(fileMeta)) {
    return { primary: "Shared only", chips: [], chipPresentations: [], lines: ["Shared only"] };
  }

  if (!requests.length && needsClientApproval(fileMeta)) {
    const kind = safeStr(fileMeta?.reviewRequestKind).toLowerCase() === "approval" ? "approval" : "review";
    const primary = kind === "approval" ? "Waiting on client (legacy)" : "Waiting on collaborators (legacy)";
    return { primary, chips: [], chipPresentations: [], lines: [primary] };
  }

  const open = requests.filter(
    (r) => r.status === REVIEW_REQUEST_STATUSES.OPEN && r.requestType !== REVIEW_REQUEST_TYPES.NONE
  );
  const primary = open.length
    ? lines.filter((l) => l.startsWith("Waiting on")).join(" · ") || "Action required"
    : lines.length
      ? lines.slice(0, 2).join(" · ")
      : "Shared only";

  return {
    primary: primary.length > 72 ? `${primary.slice(0, 70)}…` : primary,
    chips: lines.slice(0, 4),
    chipPresentations: chipPresentations.slice(0, 4),
    lines,
  };
}

/** Project-level waiting-on counts from per-collaborator open requests. */
export function buildAdminReviewWaitingOnSummary(files = [], shares = [], collaborators = []) {
  const counts = { client: 0, engineer: 0, designer: 0, other: 0, unshared: 0, totalPending: 0 };
  const seen = { client: new Set(), engineer: new Set(), designer: new Set(), other: new Set() };

  for (const file of files || []) {
    const fid = safeStr(file?.id);
    if (!fid || safeStr(file?.status).toLowerCase() === "deleted") continue;

    const requests = normalizeReviewRequests(file);
    const open = requests.filter(
      (r) => r.status === REVIEW_REQUEST_STATUSES.OPEN && r.requestType !== REVIEW_REQUEST_TYPES.NONE
    );

    if (open.length) {
      counts.totalPending += 1;
      const buckets = new Set(open.map((r) => roleBucket(r.role)));
      for (const b of buckets) {
        if (!seen[b]) seen[b] = new Set();
        if (!seen[b].has(fid)) {
          seen[b].add(fid);
          counts[b] += 1;
        }
      }
      continue;
    }

    if (needsClientApproval(file)) {
      counts.totalPending += 1;
      const fileShares = (shares || []).filter((s) => safeStr(s.fileId) === fid);
      if (!fileShares.length) {
        counts.unshared += 1;
        continue;
      }
      const bucketsHit = new Set();
      for (const share of fileShares) {
        const uid = safeStr(share?.sharedWithUid);
        const email = safeEmail(share?.sharedWithEmail || share?.sharedWithEmailLower);
        const collab =
          (collaborators || []).find((c) => safeStr(c?.uid || c?.userId) === uid) ||
          (collaborators || []).find((c) => safeEmail(c?.email || c?.emailLower) === email);
        bucketsHit.add(roleBucket(collab?.role || share?.role));
      }
      for (const b of bucketsHit) {
        if (!seen[b]) seen[b] = new Set();
        if (!seen[b].has(fid)) {
          seen[b].add(fid);
          counts[b] += 1;
        }
      }
    }
  }

  return counts;
}

export function buildReviewRequestsFirestorePayload(requests, legacySync) {
  const list = (requests || []).map(({ _index, ...rest }) => rest);
  const payload = { reviewRequests: list };
  if (legacySync?.reviewStatus) payload.reviewStatus = legacySync.reviewStatus;
  if (legacySync?.clearLegacyRequest) {
    payload.reviewRequestKind = null;
    payload.clearLegacyRequest = true;
  } else if (legacySync?.reviewRequestKind) {
    payload.reviewRequestKind = legacySync.reviewRequestKind;
  }
  return payload;
}
